import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import Lazy from 'lazy.js';
import { Component } from 'react';
import { History, Location } from 'history';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { getTournament } from 'Src/helpers/service/adminSU/tournamentsTable';
import { parse } from 'query-string';
import { getAllSchoolUnionTournamentTeams } from 'Src/helpers/service/adminSU/tournamentTeamsTable';
import { Tournament, TournamentCustomGroup, TournamentLayout } from 'Src/models/tournament';
import { TOURNAMENT_GROUP_TAG_MAPPING, TOURNAMENT_LAYOUT_TYPE } from 'Src/consts/tournament';
import { GENDER, SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/common';
import { AGE_GROUPS, KIND } from 'Src/consts/school';
import { getAllSchoolUnionSchools } from 'Src/helpers/service/adminSU/schoolsTable';
import { Select } from 'Src/components/Select/Select';
import TeamCard from './TeamCard';
import { TeamDistributedCard } from './TeamDistributedCard';
import TeamDropSection from './TeamDropSection';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import './TournamentGrid.css';
import { addTournamentTeamGroupIds } from 'Src/helpers/service/adminSU/tournamentGrid';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}
interface State {
  isLoading: boolean;
  tournament: Tournament;
  teams: TournamentGridShortTeamData[];
  activeGroupIndex: number;
  activeGroupId: string;
  activeFilterPlaces: string[];
  isDirty: boolean;
  schoolSearch: string;
  schoolAppliedSearch: string;
  groupTeamOrders: Record<string, string[]>;
  isSaveSuccessOpen: boolean;
}

export interface TournamentGridShortTeamData {
  schoolPic?: string;
  teamAges: number[];
  teamGender: string;
  schoolName: string;
  teamName: string;
  teamId: string;
  teamGroupTag: string | null;
  groupIds?: string[];
  groupResults: string;
  place: string;
  areas: string[];
}

interface GroupAdditionalData {
  groupAgeGenderAndName: string;
  groupAgeGenderAndId: string;
  groupAgeGroupNameGroupGenderAndStageName: string;
  groupId: string;
  groupAge: number;
  groupName: string;
}

const PLACE_IN_GROUP = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'];
//copy-paste from old frontend
class AdminSUTournamentGrid extends Component<Props, State> {
  GROUP_ADDITIONAL_DATA_LIST: GroupAdditionalData[] = [];

  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: false,
      tournament: undefined,
      teams: [],
      activeGroupIndex: 0,
      activeGroupId: '',
      activeFilterPlaces: [],
      isDirty: false,
      schoolSearch: '',
      schoolAppliedSearch: '',
      groupTeamOrders: {},
      isSaveSuccessOpen: false
    };
  }

  static getSortedTeams(teams = []) {
    return [...teams].sort((team1, team2) => {
      const points1 = propz.get(team1, ['results', 'group', 'points'], 0);
      const points2 = propz.get(team2, ['results', 'group', 'points'], 0);
      return points2 - points1;
    });
  }

  static getPlaceInGroup(team, teamsSorted, groupName): string {
    const teamIndex = teamsSorted
      .filter(teamSorted => teamSorted.results.group.name === groupName)
      .findIndex(teamSorted => teamSorted.id === team.id);

    return teamIndex !== -1 ? PLACE_IN_GROUP[teamIndex] : '';
  }

  getTournamentId(): string {
    const { history } = this.props;
    const search = parse(history.location.search);
    const tournamentId = search.tournament;
    return tournamentId;
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    const { user } = this.props;
    const tournamentId = this.getTournamentId();

    const tournamentsPromise = getTournament(user, tournamentId);
    const tournamentTeamsPromise = getAllSchoolUnionTournamentTeams(user, tournamentId);
    const areasPromise = getAllSchoolUnionSchools(user, { kind: { $in: [KIND.SCHOOL_UNION] } }).catch(() => []);

    BPromise.all([tournamentsPromise, tournamentTeamsPromise, areasPromise])
      .then(([tournament, teams, areas]) => {
        const teamsSorted = AdminSUTournamentGrid.getSortedTeams(teams);

        const shortTeams: TournamentGridShortTeamData[] = teams.map(team => {
          const { total, won, lost, drew, points, name: groupName } = team.results.group;
          const place = total !== 0 ? AdminSUTournamentGrid.getPlaceInGroup(team, teamsSorted, groupName) : '';
          const groupResults =
            total !== 0 ? `${groupName} ${place} (P: ${total}, W:${won}, D:${lost}, L:${drew}, Pts:${points})` : '';

          const { name, ages, gender, id, groupTag, groupIds } = team;
          const schoolPic = propz.get(team, ['school', 'pic']);
          const schoolName = propz.get(team, ['school', 'name']);
          const schoolId = String(propz.get(team, ['school', 'id']) || propz.get(team, ['schoolId']) || '');
          const validGroupIds = new Set((tournament.customGroups || []).map(group => String(group.id)));

          return {
            schoolPic: schoolPic,
            schoolName: schoolName,
            teamName: name,
            teamAges: ages,
            teamGender: gender,
            teamId: id,
            teamGroupTag: groupTag,
            groupIds: (groupIds || []).filter(groupId => validGroupIds.has(String(groupId))),
            groupResults: groupResults,
            place: place,
            areas: (areas || [])
              .filter(area =>
                String(area.id) !== String(tournament.schoolId) &&
                (area.schoolIds || []).some(areaSchoolId => String(areaSchoolId) === schoolId)
              )
              .map(area => area.name)
              .filter(Boolean)
          };
        });

        this.GROUP_ADDITIONAL_DATA_LIST = this.getGroupAdditionalDataList(tournament);

        const initialGroupId = this.GROUP_ADDITIONAL_DATA_LIST[0]?.groupId || '';

        this.setState({
          isLoading: false,
          tournament: tournament,
          teams: shortTeams,
          groupTeamOrders: (tournament.roundRobinTeamOrders || []).reduce((result, item) => ({...result,[String(item.groupId)]:item.teamIds.map(String)}), {}),
          activeGroupId: initialGroupId
        });
        window.dispatchEvent(
          new CustomEvent('sit:topbar-breadcrumb', {
            detail: `${tournament.name} / Competition / Format & Structure / Group grid view`
          })
        );
      })
      .catch(() => this.setState({ isLoading: false }));
  }

  componentWillUnmount() {
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
  }

  getGroupAdditionalDataList(tournament): GroupAdditionalData[] {
    const groups: TournamentCustomGroup[] = this.getGroups(tournament);
    const tournamentGender = tournament.gender;

    const isTournamentGenderMixed = tournamentGender === GENDER.MIXED;
    const isMixedTeamsAvailable = tournament.isMixedTeamsAvailable;

    const tournamentAges = tournament.ages;

    const groupAdditionalDataList = (Lazy(groups) as any)
      .map(group => {
        return (Lazy(tournamentAges) as any)
          .map(tournamentAge => {
            const groupMapped = TOURNAMENT_GROUP_TAG_MAPPING[group.name];
            const tournamentGroup = typeof groupMapped !== 'undefined' ? groupMapped : group.name;
            const groupStageId = group.stageId;
            const stage = tournament.tournamentLayout.find(tournamentStage => tournamentStage.id === groupStageId);
            const stageName = stage.name;
            const age = AGE_GROUPS['U5-U18'][tournamentAge];

            if (isTournamentGenderMixed && !isMixedTeamsAvailable) {
              const genderMale = SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[GENDER.MALE_ONLY];
              const genderFemale = SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[GENDER.FEMALE_ONLY];

              return [
                {
                  groupAgeGenderAndName: `${age} ${GENDER.MALE_ONLY} ${tournamentGroup}`,
                  groupAgeGenderAndId: `${age} ${GENDER.MALE_ONLY} ${group.id}`,
                  groupAgeGroupNameGroupGenderAndStageName: `${age} ${genderMale} ${tournamentGroup} [${stageName}]`,
                  groupId: group.id,
                  groupAge: group.age,
                  groupName: group.name
                },
                {
                  groupAgeGenderAndName: `${age} ${GENDER.FEMALE_ONLY} ${tournamentGroup}`,
                  groupAgeGenderAndId: `${age} ${GENDER.FEMALE_ONLY} ${group.id}`,
                  groupAgeGroupNameGroupGenderAndStageName: `${age} ${genderFemale} ${tournamentGroup} [${stageName}]`,
                  groupId: group.id,
                  groupAge: group.age,
                  groupName: group.name
                }
              ];
            } else {
              const gender = SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[tournamentGender];
              return {
                groupAgeGenderAndName: `${age} ${tournamentGender} ${tournamentGroup}`,
                groupAgeGenderAndId: `${age} ${tournamentGender} ${group.id}`,
                groupAgeGroupNameGroupGenderAndStageName: `${age} ${gender} ${tournamentGroup} [${stageName}]`,
                groupId: group.id,
                groupAge: group.age,
                groupName: group.name
              };
            }
          })
          .flatten()
          .toArray();
      })
      .flatten()
      .toArray();

    groupAdditionalDataList.sort((groupAdditionalData1, groupAdditionalData2) => {
      const stage1 = this.getStageByGroupId(tournament, groupAdditionalData1.groupId);
      const stage2 = this.getStageByGroupId(tournament, groupAdditionalData2.groupId);
      const stageRank = (type: string) => (type === TOURNAMENT_LAYOUT_TYPE.GROUP ? 0 : 1);
      const stageDifference = stageRank(stage1.type) - stageRank(stage2.type);
      if (stageDifference) return stageDifference;
      const groupDifference = String(groupAdditionalData1.groupName || '').localeCompare(
        String(groupAdditionalData2.groupName || ''),
        undefined,
        { numeric: true, sensitivity: 'base' }
      );
      if (groupDifference) return groupDifference;
      const ageDifference = Number(groupAdditionalData1.groupAge || 0) - Number(groupAdditionalData2.groupAge || 0);
      if (ageDifference) return ageDifference;
      return groupAdditionalData1.groupAgeGroupNameGroupGenderAndStageName.localeCompare(
        groupAdditionalData2.groupAgeGroupNameGroupGenderAndStageName,
        undefined,
        { numeric: true, sensitivity: 'base' }
      );
    });

    return groupAdditionalDataList;
  }

  // Grid contains only the main group stage followed by qualifier groups.
  getGroups(tournament: Tournament): TournamentCustomGroup[] {
    const structure = tournament.competitionStructure?.draft || tournament.competitionStructure?.published;
    const competitionIds = new Set([
      ...(structure?.groupOrder || []).map(String),
      ...(structure?.matches || []).map(match => String(match.groupId))
    ]);
    const customGroups = (tournament.customGroups || []).filter(customGroup => {
      if (competitionIds.has(String(customGroup.id))) return false;
      const stage = tournament.tournamentLayout.find(tournamentStage => tournamentStage.id === customGroup.stageId);
      if (typeof stage !== 'undefined') {
        const stageType = stage.type;
        const isStageTypeGroup = stageType === TOURNAMENT_LAYOUT_TYPE.GROUP;
        const isStageTypeQualifier = stageType === TOURNAMENT_LAYOUT_TYPE.QUALIFIER;
        return isStageTypeGroup || isStageTypeQualifier;
      } else {
        console.error(`Can not find stage with Id: ${customGroup.stageId}`);
        return false;
      }
    });

    const stageRank = (group: TournamentCustomGroup) => {
      const stage = tournament.tournamentLayout.find(item => item.id === group.stageId);
      return stage?.type === TOURNAMENT_LAYOUT_TYPE.GROUP ? 0 : 1;
    };
    return [...customGroups].sort((left, right) => {
      const rankDifference = stageRank(left) - stageRank(right);
      return rankDifference || String(left.name || '').localeCompare(String(right.name || ''), undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    });
  }

  getStageByGroupId(tournament: Tournament, groupId: string): TournamentLayout {
    const { customGroups = [], tournamentLayout: stages = [] } = tournament;

    const group = customGroups.find(customGroup => customGroup.id === groupId);
    const stageId = group.stageId;
    const stage = stages.find(tournamentStage => tournamentStage.id === stageId);
    return stage;
  }

  getStageTypeByGroupId(tournament: Tournament, groupId: string): string {
    const stage = this.getStageByGroupId(tournament, groupId);
    const stageType = stage.type;

    return stageType;
  }

  isGroupIdQualifierStage(groupId: string): boolean {
    const { tournament } = this.state;
    const stageType = this.getStageTypeByGroupId(tournament, groupId);

    return stageType === TOURNAMENT_LAYOUT_TYPE.QUALIFIER;
  }

  isGroupIdPlayoffStage(groupId: string): boolean {
    const { tournament } = this.state;
    const stageType = this.getStageTypeByGroupId(tournament, groupId);

    return stageType === TOURNAMENT_LAYOUT_TYPE.PLAYOFF;
  }

  onFilterChange = (value: string): void => {
    this.setState(({ activeFilterPlaces }) => ({
      activeFilterPlaces:
        value === 'All'
          ? []
          : activeFilterPlaces.includes(value)
          ? activeFilterPlaces.filter(place => place !== value)
          : activeFilterPlaces.concat(value)
    }));
  };

  getTeamStages = (team: TournamentGridShortTeamData, tournament: Tournament): TournamentLayout[] => {
    const stages: TournamentLayout[] = [];
    (team.groupIds || []).forEach(groupId => {
      const group = (tournament.customGroups || []).find(customGroup => customGroup.id === groupId);
      if (!group) return;
      const stage = (tournament.tournamentLayout || []).find(item => item.id === group.stageId);
      if (stage && !stages.some(item => item.id === stage.id)) stages.push(stage);
    });
    return stages;
  };

  renderTeamCards() {
    const { activeGroupId, activeGroupIndex, teams, activeFilterPlaces, tournament } = this.state;
    const groupAgeGenderAndNameList = this.GROUP_ADDITIONAL_DATA_LIST.map(
      additionalData => additionalData.groupAgeGenderAndName
    );
    const activeGroupContext = groupAgeGenderAndNameList[activeGroupIndex];
    const activeGroup = (tournament.customGroups || []).find(customGroup => customGroup.id === activeGroupId);
    const activeStage = activeGroup
      ? (tournament.tournamentLayout || []).find(tournamentStage => tournamentStage.id === activeGroup.stageId)
      : undefined;
    if (!activeGroupContext || !activeStage) return [];
    const [currentAge, currentGender] = activeGroupContext.split(` `);

    const schoolSearch = this.state.schoolAppliedSearch.trim().toLowerCase();
    const teamsFiltered = teams.filter(team => {
      const { teamAges } = team;

      const teamAgesMapped = (Lazy(teamAges) as any).map(teamAge => AGE_GROUPS['U5-U18'][teamAge]).toArray();

      const isTeamAgeEqualSelectorAge = teamAgesMapped.some(teamAge => teamAge === currentAge);
      const isTeamGenderEqualSelectorGender = team.teamGender === currentGender;

      const stageType = activeStage.type;
      const stagesUniq = this.getTeamStages(team, tournament);

      const isTeamGroupStagesConsistInActiveGroupStage = stagesUniq.some(stage => stage.type === stageType);

      const isTeamPlaceInActiveFilterPlace =
        activeFilterPlaces.length === 0 || activeFilterPlaces.includes(team.place);

      return (
        isTeamAgeEqualSelectorAge &&
        isTeamGenderEqualSelectorGender &&
        !isTeamGroupStagesConsistInActiveGroupStage &&
        isTeamPlaceInActiveFilterPlace &&
        (!schoolSearch || String(team.schoolName || '').toLowerCase().includes(schoolSearch))
      );
    });

    return teamsFiltered.map(team => {
      return <TeamCard team={team} key={team.teamId} />;
    });
  }

  onTeamDrop = (teamId: string): void => {
    const { teams, activeGroupId } = this.state;

    const teamIndex = teams.findIndex(team => team.teamId === teamId);

    const groupIds = [].concat(teams[teamIndex].groupIds, activeGroupId);

    teams[teamIndex].groupIds = groupIds;

    this.setState(state=>({
      teams: teams,
      groupTeamOrders:{...state.groupTeamOrders,[String(activeGroupId)]:[...(state.groupTeamOrders[String(activeGroupId)]||[]).filter(id=>id!==String(teamId)),String(teamId)]},
      isDirty: true
    }));
  };

  onSelectGroupChange = (groupAgeAndId: string): void => {
    const groupAgeGenderAndIdList = this.GROUP_ADDITIONAL_DATA_LIST.map(
      additionalData => additionalData.groupAgeGenderAndId
    );
    const groupIdList = this.GROUP_ADDITIONAL_DATA_LIST.map(additionalData => additionalData.groupId);

    const activeGroupIndex = groupAgeGenderAndIdList.findIndex(id => id === groupAgeAndId);

    const currentGroupId = groupIdList[activeGroupIndex];

    this.setState({
      activeGroupIndex: activeGroupIndex,
      activeGroupId: currentGroupId
    });
  };

  renderTeamDistributedCards() {
    const { activeGroupId, activeGroupIndex, teams, tournament } = this.state;

    const groupAgeGenderAndNameList = this.GROUP_ADDITIONAL_DATA_LIST.map(
      additionalData => additionalData.groupAgeGenderAndName
    );

    const activeGroupContext = groupAgeGenderAndNameList[activeGroupIndex];
    const activeGroup = (tournament.customGroups || []).find(customGroup => customGroup.id === activeGroupId);
    const activeStage = activeGroup
      ? (tournament.tournamentLayout || []).find(tournamentStage => tournamentStage.id === activeGroup.stageId)
      : undefined;
    if (!activeGroupContext || !activeStage) return [];
    const [currentAge, currentGender] = activeGroupContext.split(` `);

    const schoolSearch = this.state.schoolAppliedSearch.trim().toLowerCase();
    const teamsDistributedFiltered = teams.filter(team => {
      const { teamAges } = team;

      const teamAgesMapped = (Lazy(teamAges) as any).map(teamAge => AGE_GROUPS['U5-U18'][teamAge]).toArray();

      const stagesUniq = this.getTeamStages(team, tournament);
      const stageType = activeStage.type;

      const isTeamAgeEqualSelectorAge = teamAgesMapped.some(teamAge => teamAge === currentAge);
      const isTeamGenderEqualSelectorGender = team.teamGender === currentGender;
      const isTeamGroupStagesConsistInSelectorStage = stagesUniq.some(stage => stage.type === stageType);
      const isTeamGroupIdConsistInGroupIdSelector = (team.groupIds || []).some(groupId => groupId === activeGroupId);

      return (
        isTeamAgeEqualSelectorAge &&
        isTeamGenderEqualSelectorGender &&
        isTeamGroupStagesConsistInSelectorStage &&
        isTeamGroupIdConsistInGroupIdSelector &&
        (!schoolSearch || String(team.schoolName || '').toLowerCase().includes(schoolSearch))
      );
    });
    const savedOrder=this.state.groupTeamOrders[String(activeGroupId)]||[];
    teamsDistributedFiltered.sort((left,right)=>{const leftIndex=savedOrder.indexOf(String(left.teamId));const rightIndex=savedOrder.indexOf(String(right.teamId));return(leftIndex<0?Number.MAX_SAFE_INTEGER:leftIndex)-(rightIndex<0?Number.MAX_SAFE_INTEGER:rightIndex);});

    if (teamsDistributedFiltered.length === 0) {
      return (
        <div className="tournamentGridModern__emptyDrop">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3v12M7 10l5 5 5-5M5 20h14" />
          </svg>
          <strong>Drop teams here</strong>
          <span>Drag a team from the available list to assign it to this group.</span>
        </div>
      );
    }

    return teamsDistributedFiltered.map((team,index) => {
      return <TeamDistributedCard team={team} key={team.teamId} index={index} count={teamsDistributedFiltered.length} onMove={(teamId,direction)=>this.moveDistributedTeam(teamId,direction,teamsDistributedFiltered.map(item=>String(item.teamId)))} onDeleteClick={this.onDistributedCardDeleteClick} />;
    });
  }

  moveDistributedTeam=(teamId:string,direction:-1|1,visibleIds:string[])=>{const groupId=String(this.state.activeGroupId);const visible=[...visibleIds];const index=visible.indexOf(String(teamId));const target=index+direction;if(index<0||target<0||target>=visible.length)return;const full=this.getOrderedTeamIdsForGroup(groupId);const left=full.indexOf(visible[index]),right=full.indexOf(visible[target]);[full[left],full[right]]=[full[right],full[left]];this.setState(state=>({groupTeamOrders:{...state.groupTeamOrders,[groupId]:full},isDirty:true}));};

  getOrderedTeamIdsForGroup=(groupId:string)=>{const assigned=this.state.teams.filter(team=>(team.groupIds||[]).map(String).includes(String(groupId))).map(team=>String(team.teamId));const saved=this.state.groupTeamOrders[String(groupId)]||[];return[...saved.filter(id=>assigned.includes(id)),...assigned.filter(id=>!saved.includes(id))];};

  onDistributedCardDeleteClick = (teamId: string): void => {
    const { teams, activeGroupId } = this.state;

    const teamIndex = teams.findIndex(team => team.teamId === teamId);
    const groupIndex = teams[teamIndex].groupIds.findIndex(groupId => groupId === activeGroupId);

    const groupIds = teams[teamIndex].groupIds;
    groupIds.splice(groupIndex, 1);

    teams[teamIndex].groupIds = groupIds;

    this.setState(state=>({
      teams: teams,
      groupTeamOrders:{...state.groupTeamOrders,[String(activeGroupId)]:(state.groupTeamOrders[String(activeGroupId)]||[]).filter(id=>id!==String(teamId))},
      isDirty: true
    }));
  };

  goBack = () => {
    const { history, location } = this.props;
    const { state } = location;
    const {
      search = `?tournament=${this.getTournamentId()}`,
      prevPath = '/tournaments/manage/competition/groups'
    } = (state || {}) as any;
    const tournamentId = this.getTournamentId();

    history.push({
      pathname: prevPath,
      search: search,
      state: { tournamentId: tournamentId }
    });
  };

  onSubmit = () => {
    const { user } = this.props;
    const { teams } = this.state;
    const tournamentId = this.getTournamentId();

    const allTeamsData = {
      teams: teams.map(team => {
        return {
          teamId: team.teamId,
          groupIds: team.groupIds
        };
      }),
      groupTeamOrders: (this.state.tournament.customGroups||[]).map(group=>({groupId:group.id,teamIds:this.getOrderedTeamIdsForGroup(group.id)}))
    };

    this.setState({
      isLoading: true
    });

    addTournamentTeamGroupIds(user, tournamentId, allTeamsData)
      .then(() => this.setState({ isLoading: false, isDirty: false, isSaveSuccessOpen: true }))
      .catch(() => this.setState({ isLoading: false }));
  };

  renderPageHeader = () => {
    const { tournament, teams } = this.state;
    const groups = tournament ? this.getGroups(tournament) : [];
    const gridGroupIds = new Set(groups.map(group => String(group.id)));
    const unassignedCount = teams.filter(
      team => !(team.groupIds || []).some(groupId => gridGroupIds.has(String(groupId)))
    ).length;
    return (
      <header className="tournamentGridModern__pageHeader">
        <button type="button" className="tournamentGridModern__back" onClick={this.goBack}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
          Back to Format &amp; Structure
        </button>
        <div className="tournamentGridModern__headerMain">
          <div><span>Team group grid</span><h1>{tournament ? tournament.name : 'Tournament groups'}</h1></div>
          <div className="tournamentGridModern__summary">
            {groups.map(group => {
              const count = teams.filter(team => (team.groupIds || []).includes(group.id)).length;
              return <span key={group.id}><strong>{group.name}</strong> · {count} {count === 1 ? 'team' : 'teams'}</span>;
            })}
            <span className={unassignedCount ? 'is-warning' : ''}>
              <strong>{unassignedCount}</strong> {unassignedCount === 1 ? 'team' : 'teams'} not assigned
            </span>
          </div>
        </div>
      </header>
    );
  };

  render() {
    const { isLoading, tournament } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    if (this.GROUP_ADDITIONAL_DATA_LIST.length === 0) {
      return (
        <div className="tournamentGridModern">
          {this.renderPageHeader()}
          <main className="tournamentGridModern__workspace">
            <section className="tournamentGridModern__card tournamentGridModern__noGroups">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" />
              </svg>
              <h2>No groups available</h2>
              <p>Create tournament groups before assigning teams in the grid.</p>
              <button type="button" onClick={this.goBack}>
                Return to Format &amp; Structure
              </button>
            </section>
          </main>
        </div>
      );
    }

    const { activeGroupIndex, activeFilterPlaces } = this.state;
    const groupAgeGroupNameAndStageNameList = this.GROUP_ADDITIONAL_DATA_LIST.map(additionalData => {
      return {
        text: additionalData.groupAgeGroupNameGroupGenderAndStageName,
        value: additionalData.groupAgeGenderAndId
      };
    });

    const activeGroup = this.GROUP_ADDITIONAL_DATA_LIST[activeGroupIndex];
    const activeGroupId = activeGroup.groupId;
    const isGroupIdQualifierStage = this.isGroupIdQualifierStage(activeGroupId);
    const isGroupIdPlayoffStage = this.isGroupIdPlayoffStage(activeGroupId);
    const isGroupIdQualifierOrPlayoff = isGroupIdQualifierStage || isGroupIdPlayoffStage;

    const availableTeamCards = this.renderTeamCards();
    const distributedTeamCards = this.renderTeamDistributedCards();
    const availableCount = React.Children.count(availableTeamCards);
    const assignedCount = Array.isArray(distributedTeamCards) ? distributedTeamCards.length : 0;
    const activeGroupLabel = activeGroup.groupAgeGroupNameGroupGenderAndStageName;

    return (
      <div className="tournamentGridModern">
        {this.renderPageHeader()}

        <main className="tournamentGridModern__workspace">
          <section className="tournamentGridModern__card">
            <header className="tournamentGridModern__cardHeader">
              <div className="tournamentGridModern__cardTitleTools">
                <h2>Assign teams to groups</h2>
                <div className="tournamentGridModern__search">
                  <input
                    value={this.state.schoolSearch}
                    placeholder="Search by school name"
                    onChange={event => this.setState({ schoolSearch: event.target.value })}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        this.setState({ schoolAppliedSearch: this.state.schoolSearch });
                      }
                    }}
                  />
                  {this.state.schoolSearch ? (
                    <button
                      type="button"
                      aria-label="Clear school search"
                      onClick={() => this.setState({ schoolSearch: '', schoolAppliedSearch: '' })}
                    >
                      ×
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="tournamentGridModern__cardTools">
                <label className="tournamentGridModern__groupControl">
                  <span>Group</span>
                  <Select
                    options={groupAgeGroupNameAndStageNameList}
                    onChange={this.onSelectGroupChange}
                    activeResultIndex={activeGroupIndex}
                    customClass="tournamentGridModern__select"
                  />
                </label>
                {this.state.isDirty ? <span className="tournamentGridModern__unsaved">Unsaved changes</span> : null}
              </div>
            </header>

            {isGroupIdQualifierOrPlayoff ? (
              <div className="tournamentGridModern__controls">
                <div className="tournamentGridModern__placeFilter">
                  <span>Previous group finish</span>
                  <div className="tournamentGridModern__placeChips">
                    {['All'].concat(PLACE_IN_GROUP).map(place => {
                      const isActive =
                        place === 'All' ? activeFilterPlaces.length === 0 : activeFilterPlaces.includes(place);

                      return (
                        <button
                          type="button"
                          key={place}
                          className={isActive ? 'is-active' : ''}
                          aria-pressed={isActive}
                          onClick={() => this.onFilterChange(place)}
                        >
                          {place}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="tournamentGridModern__panels">
              <section className="tournamentGridModern__panel">
                <header>
                  <div>
                    <h3>Available teams</h3>
                    <p>Eligible for the selected age, gender and stage.</p>
                  </div>
                  <span>{availableCount}</span>
                </header>
                <div className="tournamentGridModern__teamList">
                  {availableCount ? (
                    availableTeamCards
                  ) : (
                    <div className="tournamentGridModern__emptyList">No eligible teams available.</div>
                  )}
                </div>
              </section>

              <div className="tournamentGridModern__direction" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </div>

              <TeamDropSection onTeamDrop={this.onTeamDrop}>
                <header>
                  <div>
                    <h3>Assigned to group</h3>
                    <p title={activeGroupLabel}>{activeGroupLabel}</p>
                    <small>Order controls the round-robin fixture sequence.</small>
                  </div>
                  <span>{assignedCount}</span>
                </header>
                <div className="tournamentGridModern__teamList">{distributedTeamCards}</div>
              </TeamDropSection>
            </div>

            <footer className="tournamentGridModern__footer">
              <button type="button" className="is-secondary" onClick={this.goBack}>
                Cancel
              </button>
              <button type="button" className="is-primary" disabled={!this.state.isDirty} onClick={this.onSubmit}>
                Save changes
              </button>
            </footer>
          </section>
        </main>
        <SimpleModal
          isOpen={this.state.isSaveSuccessOpen}
          title="Group grid saved"
          body="Team group assignments and round-robin order have been saved successfully."
          buttonText="Done"
          hideCancelButton
          customClass="unionTournamentDeleteModal"
          dialogStyle={{ maxWidth: 420 }}
          onButtonClick={() => this.setState({ isSaveSuccessOpen: false })}
          onCloseClick={() => this.setState({ isSaveSuccessOpen: false })}
        />
      </div>
    );
  }
}

const AdminSUTournamentGridWithDnd: React.FC<Props> = props => (
  <DndProvider backend={HTML5Backend}>
    <AdminSUTournamentGrid {...props} />
  </DndProvider>
);

export default AdminSUTournamentGridWithDnd;
